// src/common/reports/ReportDetails.tsx

import React, { useEffect, useMemo, useState, ReactElement, useCallback } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import toast from "react-hot-toast";
import { httpRequest } from 'src/services/httpRequest';
import {
  downloadAuthenticatedFile,
  SessionExpiredError
} from 'src/utils/authenticatedFetch';
//import { useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';

interface CardData {
  icon: ReactElement;
  value: number | string;
  cardTitle: string;
  backgroundColor: string;
  iconColor: string;
}

interface SummaryData {
  [key: string]: number;
}

interface ReportDetailsProps {
  title: string;
  /** When omitted with reportCards/dataMapping, summary stats are not fetched or shown. */
  apiEndpoint?: string;
  downloadEndpoint?: string;
  reportCards?: (summaryData: SummaryData) => CardData[];
  dataMapping?: (responseData: any) => SummaryData;
}

const ReportDetails: React.FC<ReportDetailsProps> = ({
  title,
  apiEndpoint,
  downloadEndpoint,
  reportCards,
  dataMapping
}) => {

  const getCurrentMonthFirstDay = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  };

  const [selectedDate, setSelectedDate] = useState(getCurrentMonthFirstDay());
  const [summaryData, setSummaryData] = useState<SummaryData>({});
  const [selectedPetType, setSelectedPetType] = useState('Dog');
  const petTypes = ['Dog'];
  const { permit } = useParams();
  const cardData = useMemo(
    () => (reportCards ? reportCards(summaryData) : []),
    [reportCards, summaryData],
  );
  const showSummaryCards = Boolean(reportCards && dataMapping && apiEndpoint);

  const StatCard = ({
    icon,
    value,
    cardTitle,
    backgroundColor,
    iconColor,
  }: CardData) => (
    <Paper sx={{ p: 5, bgcolor: backgroundColor, flexGrow: 1, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {React.cloneElement(icon, { sx: { color: iconColor, mr: 2, fontSize: '2.8rem' } })}
        <Box >
          <Typography variant="h4" fontWeight="bold">{value}</Typography>
          <Typography variant="subtitle1">{cardTitle}</Typography>
        </Box>
      </Box>
    </Paper>
  );

  const formatDateShort = (date: Date): string => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const getMonthDateRange = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return {
      startDate: formatDateShort(new Date(year, month, 1)),
      endDate: formatDateShort(new Date(year, month + 1, 0)),
    };
  };

  const { startDate, endDate } = useMemo(() => getMonthDateRange(selectedDate), [selectedDate]);

  const fetchSummaryData = useCallback(async () => {
    if (!apiEndpoint || !dataMapping) {
      return;
    }
    try {

      const apiUrl = permit !== '10'
        ? `${apiEndpoint}?startDate=${startDate}&endDate=${endDate}`
        : `${apiEndpoint}/${permit}/get_report?petType=${selectedPetType}&startDate=${startDate}&endDate=${endDate}`;
      // Send the API request
      const response = await httpRequest('GET', apiUrl);
      // Handle the response
      if (response?.status === "success" && response.data) {
        const mappedData = dataMapping(response.data);
        setSummaryData(mappedData);
      } else {
        toast.error(response?.message || 'Failed to fetch report data');
      }
    } catch (error: any) {
      console.error('Error occurred:', error);  // Log any errors
      toast.error(error?.message || 'An error occurred');
    }
  }, [apiEndpoint, dataMapping, permit, selectedPetType, startDate, endDate]);

  useEffect(() => {
    if (!showSummaryCards) {
      return;
    }
    void fetchSummaryData();
  }, [showSummaryCards, fetchSummaryData]);

  const handleDownloadReport = async () => {
    try {
      const downloadPath =
        permit === '10'
          ? `/${downloadEndpoint}/${permit}/download_report?petType=${selectedPetType}&startDate=${startDate}&endDate=${endDate}`
          : `/${downloadEndpoint}?startDate=${startDate}&endDate=${endDate}`;

      const safeTitle = title.replace(/\s+/g, '_').toLowerCase();
      await downloadAuthenticatedFile(downloadPath, `${safeTitle}_${startDate}_${endDate}.pdf`);
      toast.success('Report downloaded successfully');
    } catch (error: any) {
      if (error instanceof SessionExpiredError) {
        return;
      }
      toast.error(error?.message || 'Failed to download report');
    }
  };

  return (
    <Box>
      <Typography variant="h3" mx={3} mb={2} color="primary">
        {title}
      </Typography>

      <Container maxWidth="xl">
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {permit === '10' && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="pet-type-label">Pet Type</InputLabel>
                  <Select
                    labelId="pet-type-label"
                    value={selectedPetType}
                    onChange={(e) => setSelectedPetType(e.target.value)}
                    label="Pet Type"
                  >
                    {petTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  views={['month', 'year']}
                  label="Select Month"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue as Date)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1} alignItems="center">
                <Typography variant="body1">From:</Typography>
                <Typography variant="h6" color="primary">{startDate}</Typography>
                <Typography variant="body1">To:</Typography>
                <Typography variant="h6" color="primary">{endDate}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={permit === '10' ? 3 : 6} textAlign={permit === '10' ? { xs: 'center', md: 'right' } : 'right'}>
              <Tooltip title="Download Report" arrow>
                <IconButton color="success" onClick={handleDownloadReport}>
                  <DownloadForOfflineIcon fontSize="large" />
                </IconButton>
              </Tooltip>
            </Grid>

          </Grid>

          {showSummaryCards && cardData.length > 0 ? (
            <Grid container spacing={2} mt={1}>
              {cardData.map((card, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <StatCard {...card} />
                </Grid>
              ))}
            </Grid>
          ) : null}
        </Paper>
      </Container>
    </Box>
  );
};

export default ReportDetails;
